import axios from 'axios'
import {API_URL} from '../../../../_metronic/helpers'
import {TermsAcceptRequest, TermsAcceptResult, TermsDocument, TermsStatus} from './_models'

const TERMS_URL = `${API_URL}/terms`

// Async functions, so that anything axios throws arrives as a rejected promise.
export const getCurrentTerms = async (signal?: AbortSignal): Promise<TermsDocument> => {
  const response = await axios.get<TermsDocument>(`${TERMS_URL}/current`, {signal})
  return response.data
}

export const getTermsVersion = async (
  version: number,
  signal?: AbortSignal
): Promise<TermsDocument> => {
  const response = await axios.get<TermsDocument>(`${TERMS_URL}/versions/${version}`, {signal})
  return response.data
}

export const getTermsStatus = async (signal?: AbortSignal): Promise<TermsStatus> => {
  const response = await axios.get<TermsStatus>(`${TERMS_URL}/status`, {signal})
  return response.data
}

export const acceptTerms = async (request: TermsAcceptRequest): Promise<TermsAcceptResult> => {
  const response = await axios.post<TermsAcceptResult>(`${TERMS_URL}/accept`, request)
  return response.data
}

export const getTermsStatusQueryKey = (userId: number, companyId: number) => [
  'terms-status',
  userId,
  companyId,
]

export const getTermsDocumentQueryKey = (version?: number) => [
  'terms-document',
  version ?? 'current',
]

export const TERMS_VERSION_CHANGED = 'TERMS_VERSION_CHANGED'

type ErrorResponse = {response?: {status?: number; data?: unknown}}

const readErrorStatus = (error: unknown) =>
  Number((error as ErrorResponse | null)?.response?.status || 0)

const readErrorData = (error: unknown) => {
  const data = (error as ErrorResponse | null)?.response?.data
  return data && typeof data === 'object'
    ? (data as {code?: unknown; message?: unknown})
    : undefined
}

// A 4xx does not get better by retrying; a dropped connection may.
export const shouldRetryTermsRead = (failureCount: number, error: unknown) => {
  const status = readErrorStatus(error)
  if (status >= 400 && status < 500) return false

  return failureCount < 2
}

// The server published a newer version while this one was being read.
export const isTermsVersionChanged = (error: unknown) =>
  readErrorData(error)?.code === TERMS_VERSION_CHANGED

// Never shows a raw server answer: only the server's own readable message, or the fallback.
export const readTermsError = (error: unknown, fallback: string): string => {
  const message = readErrorData(error)?.message
  return typeof message === 'string' && message.trim() ? message.trim() : fallback
}
